// "Revive el mercado": thousands of points of light with real physics. Each
// point is a small mass on a spring towards its place in a picture; the
// pictures are drawn with real market data:
//   0  the market date, rewinding through past sessions to yesterday's close
//   1  the AAPL chain of that day, legible, with the 215 call bought at its ask
//   2  the result: +$700, sold on 29 Aug at the bid, over what the chain paid
//      for the call every session (real prices)
// The pointer pushes the points away and they spring back; changing picture
// gives them a kick and they fly to their new places.
import * as THREE from '../vendor/three/three.module.min.js';
import { callDays, CALL } from './data.js';

// AAPL, trade date 9 Aug 2024, expiry 20 Sep 2024 (Intrinio EOD, as the app shows it)
const CHAIN = [
  { k: 205, cb: 14.6, ca: 16.75, pb: 3.3, pa: 3.4 },
  { k: 210, cb: 11.9, ca: 12.1, pb: 4.6, pa: 4.75 },
  { k: 215, cb: 8.7, ca: 8.9, pb: 6.35, pa: 6.55 },
  { k: 220, cb: 6.0, ca: 6.15, pb: 8.55, pa: 8.9 },
  { k: 225, cb: 3.9, ca: 4.05, pb: 11.65, pa: 11.9 },
  { k: 230, cb: 2.4, ca: 2.5, pb: 15.15, pa: 15.5 },
];
const SPOT = 216.24;
// sessions to rewind through before landing on yesterday (all real market days)
const PAST_SESSIONS = ['2021-09-27', '2023-03-13', '2024-08-05', '2025-04-07'];

const COPY = {
  es: {
    num: (v, d = 2) => v.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }),
    calls: 'CALLS', puts: 'PUTS', strike: 'STRIKE', bid: 'BID', ask: 'ASK',
    buy: 'COMPRA', sell: 'VENDE', spot: 'AAPL',
    result: '+700 $', resultCaption: 'Call 215 · compra 8,90 · venta 15,90 el 29 ago', yesterday: 'hasta el cierre de ayer',
    buyTag: 'compra', sellTag: 'venta',
    date: (y, m, d) => `${d}/${m}/${y.slice(2)}`,
  },
  en: {
    num: (v, d = 2) => v.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }),
    calls: 'CALLS', puts: 'PUTS', strike: 'STRIKE', bid: 'BID', ask: 'ASK',
    buy: 'BUY', sell: 'SELL', spot: 'AAPL',
    result: '+$700', resultCaption: '215 call · bought 8.90 · sold 15.90 on Aug 29', yesterday: "up to yesterday's close",
    buyTag: 'buy', sellTag: 'sell',
    date: (y, m, d) => `${m}/${d}/${y.slice(2)}`,
  },
};

/* ------------------------------------------------------------- colours */
const C = (h) => new THREE.Color(h);
const SILVER = C(0xe9eef2);
const STEEL = C(0x9aa6b2);
const MUTED = C(0x55626f);
const GAIN = C(0x7ce38b);
const LOSS = C(0xff7d91);
const TEAL = C(0x6fd6e8);
const LILAC = C(0xb7a6ff);
const brandAt = (t) => (t < 0.5 ? GAIN.clone().lerp(TEAL, t * 2) : TEAL.clone().lerp(LILAC, (t - 0.5) * 2));

function rng(seed) {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

/* ------------------------------------------------------- picture drawing */
// A picture is drawn on a 1000 x 620 canvas, one flat colour per item, then
// every inked pixel becomes a possible place for a point. Its colour tells
// which item it belongs to, so each item can have its own light.
const PW = 1000;
const PH = 620;
function drawPicture(items) {
  const canvas = document.createElement('canvas');
  canvas.width = PW;
  canvas.height = PH;
  const g = canvas.getContext('2d', { willReadFrequently: true });
  items.forEach((item, i) => {
    const code = `rgb(${(i + 1) * 3}, 255, 255)`;
    g.fillStyle = code;
    g.strokeStyle = code;
    item.draw(g);
  });
  const data = g.getImageData(0, 0, PW, PH).data;
  const places = [];
  for (let y = 0; y < PH; y += 1) {
    for (let x = 0; x < PW; x += 1) {
      const o = (y * PW + x) * 4;
      if (data[o + 3] < 150) continue;
      const item = Math.round(data[o] / 3) - 1;
      if (item < 0 || item >= items.length) continue;
      places.push(x, y, item);
    }
  }
  return places;
}

const text = (str, x, y, font, align = 'center') => (g) => {
  g.font = font;
  g.textAlign = align;
  g.textBaseline = 'middle';
  g.fillText(str, x, y);
};
const box = (x, y, w, h, r = 14, lw = 4) => (g) => {
  g.lineWidth = lw;
  g.beginPath();
  g.roundRect(x - w / 2, y - h / 2, w, h, r);
  g.stroke();
};
const line = (pts, lw) => (g) => {
  g.lineWidth = lw;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  g.beginPath();
  pts.forEach(([x, y], i) => (i ? g.lineTo(x, y) : g.moveTo(x, y)));
  g.stroke();
};

function datePicture(ymd, t) {
  const [y, m, d] = ymd.split('-');
  const label = t.date(y, m, d);
  return [
    { draw: text(label, 500, 270, '700 200px "Fira Code", ui-monospace, Menlo, monospace'), color: (x) => brandAt(x / PW).lerp(SILVER, 0.35), weight: 1 },
    { draw: text(t.yesterday, 500, 450, '700 46px Manrope, system-ui, sans-serif'), color: () => SILVER.clone().lerp(STEEL, 0.35), weight: 1.25 },
  ];
}

function chainPicture(t) {
  const mono = (px, w = 600) => `${w} ${px}px "Fira Code", ui-monospace, Menlo, monospace`;
  const cols = { cb: 100, ca: 290, k: 500, pb: 710, pa: 900 };
  const head = (str, x, color) => ({ draw: text(str, x, 34, mono(24, 700)), color: () => color, weight: 1.05 });
  const items = [
    head(`CALL ${t.bid}`, cols.cb, GAIN.clone().lerp(STEEL, 0.4)),
    head(`CALL ${t.ask}`, cols.ca, GAIN.clone().lerp(STEEL, 0.4)),
    head(t.strike, cols.k, STEEL),
    head(`PUT ${t.bid}`, cols.pb, TEAL.clone().lerp(STEEL, 0.4)),
    head(`PUT ${t.ask}`, cols.pa, TEAL.clone().lerp(STEEL, 0.4)),
  ];
  let y = 104;
  CHAIN.forEach((row) => {
    const buy = row.k === CALL.strike;
    const sell = false;
    const callC = SILVER.clone().lerp(GAIN, 0.22);
    const putC = SILVER.clone().lerp(TEAL, 0.22);
    items.push({ draw: text(t.num(row.cb), cols.cb, y, mono(46)), color: () => (sell ? LOSS : callC), weight: sell ? 1.5 : 1 });
    items.push({ draw: text(t.num(row.ca), cols.ca, y, mono(46)), color: () => (buy ? GAIN : callC), weight: buy ? 1.5 : 1 });
    items.push({ draw: text(t.num(row.k, 0), cols.k, y, mono(50, 700)), color: () => SILVER, weight: 1.05 });
    items.push({ draw: text(t.num(row.pb), cols.pb, y, mono(46)), color: () => putC, weight: 1 });
    items.push({ draw: text(t.num(row.pa), cols.pa, y, mono(46)), color: () => putC, weight: 1 });
    if (buy) items.push({ draw: box(cols.ca, y, 168, 62, 16, 4), color: () => GAIN, weight: 0.8 });
    if (sell) items.push({ draw: box(cols.cb, y, 168, 62, 16, 4), color: () => LOSS, weight: 0.8 });
    y += 76;
    if (row.k === 215) {
      // the underlying's close sits between the 215 and 220 strikes
      items.push({ draw: text(`${t.spot} ${t.num(SPOT)}`, cols.k, y - 12, mono(32, 700)), color: () => TEAL, weight: 1.35 });
      items.push({ draw: line([[40, y - 12], [370, y - 12]], 2), color: () => TEAL.clone().multiplyScalar(0.5), weight: 0.3 });
      items.push({ draw: line([[630, y - 12], [960, y - 12]], 2), color: () => TEAL.clone().multiplyScalar(0.5), weight: 0.3 });
      y += 52;
    }
  });
  return items;
}

function resultPicture(t) {
  // what the chain paid for the 215 call every session (its bid; intrinsic at
  // expiry), from 9 Aug to 20 Sep 2024, with the buy and the sale on 29 Aug
  const days = callDays();
  const X = (i) => 90 + (i / (days.length - 1)) * 820;
  const Y = (v) => 520 - (v / 17) * 300;
  const pts = days.map((d, i) => [X(i), Y(d.value)]);
  const sold = days.findIndex((d) => d.date === CALL.sell);
  const dot = (i, r) => (g) => {
    g.beginPath();
    g.arc(X(i), Y(days[i].value), r, 0, Math.PI * 2);
    g.fill();
  };
  return [
    { draw: text(t.result, 690, 118, '800 150px Manrope, system-ui, sans-serif'), color: () => GAIN.clone().lerp(SILVER, 0.15), weight: 1.2 },
    // the price paid, as a guide
    { draw: line([[90, Y(CALL.paid)], [910, Y(CALL.paid)]], 2), color: () => MUTED, weight: 0.35 },
    { draw: line(pts, 7), color: (x) => brandAt(Math.min(1, x / PW)).lerp(SILVER, 0.1), weight: 1.1 },
    { draw: dot(0, 14), color: () => SILVER, weight: 0.8 },
    { draw: dot(sold, 18), color: () => GAIN, weight: 0.9 },
    { draw: text(t.buyTag, X(0), Y(days[0].value) + 44, '700 28px "Fira Code", monospace'), color: () => SILVER.clone().lerp(STEEL, 0.4), weight: 1.1 },
    { draw: text(t.sellTag, X(sold), Y(days[sold].value) - 40, '700 28px "Fira Code", monospace'), color: () => GAIN.clone().lerp(SILVER, 0.3), weight: 1.1 },
    { draw: text(t.resultCaption, 500, 592, '700 34px Manrope, system-ui, sans-serif'), color: () => SILVER.clone().lerp(STEEL, 0.3), weight: 1.25 },
  ];
}

// Turns a picture into exactly n targets (normalised to x ±1, y ±0.62) and
// colours; items with more weight get more points per inked pixel.
function targetsFor(items, n, random) {
  const places = drawPicture(items);
  const count = places.length / 3;
  const weights = new Float32Array(count);
  let total = 0;
  for (let i = 0; i < count; i++) {
    const w = items[places[i * 3 + 2]].weight;
    weights[i] = w;
    total += w;
  }
  const pos = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  // systematic sampling over the cumulative weights: even coverage, no clumps
  const stepW = total / n;
  let acc = random() * stepW;
  let j = 0;
  let cum = weights[0];
  for (let p = 0; p < n; p++) {
    while (cum < acc && j < count - 1) cum += weights[++j];
    const x = places[j * 3] + (random() - 0.5) * 0.6;
    const y = places[j * 3 + 1] + (random() - 0.5) * 0.6;
    const item = items[places[j * 3 + 2]];
    pos[p * 3] = (x / PW) * 2 - 1;
    pos[p * 3 + 1] = (0.5 - y / PH) * (2 * PH / PW);
    pos[p * 3 + 2] = (random() - 0.5) * 0.04;
    const c = item.color(x, y);
    col[p * 3] = c.r;
    col[p * 3 + 1] = c.g;
    col[p * 3 + 2] = c.b;
    acc += stepW;
  }
  // shuffle so that points fly across the picture when it changes
  for (let i = n - 1; i > 0; i--) {
    const k = Math.floor(random() * (i + 1));
    for (let a = 0; a < 3; a++) {
      [pos[i * 3 + a], pos[k * 3 + a]] = [pos[k * 3 + a], pos[i * 3 + a]];
      [col[i * 3 + a], col[k * 3 + a]] = [col[k * 3 + a], col[i * 3 + a]];
    }
  }
  return { pos, col };
}

const VERT = /* glsl */ `
  attribute vec3 color;
  attribute float size;
  uniform float uPixelRatio;
  uniform float uSizeScale;
  varying vec3 vColor;
  void main() {
    vColor = color;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = size * uSizeScale * uPixelRatio * (10.0 / -mv.z);
  }
`;
const FRAG = /* glsl */ `
  uniform float uHalo;
  varying vec3 vColor;
  void main() {
    float r = length(gl_PointCoord - 0.5);
    float core = smoothstep(0.28, 0.0, r) * 1.35;
    float halo = smoothstep(0.5, 0.06, r) * uHalo;
    gl_FragColor = vec4(vColor, core + halo);
  }
`;

/* --------------------------------------------------------------- scene */
export async function createParticles(canvas, { onStep, reduced = false, lang = 'es' } = {}) {
  const t = COPY[lang] || COPY.es;
  const mobile = matchMedia('(max-width: 820px)').matches;
  const N = mobile ? 6500 : 14000;
  const random = rng(20240809);
  try {
    await Promise.all([
      document.fonts.load('700 200px "Fira Code"'),
      document.fonts.load('800 150px Manrope'),
      document.fonts.load('600 44px Manrope'),
    ]);
  } catch {
    /* fallbacks draw the same shapes */
  }
  // yesterday's session in the visitor's calendar (the data is updated every day)
  const d = new Date(Date.now() - 86400000);
  const yesterday = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const dates = [...PAST_SESSIONS, yesterday].map((ymd) => targetsFor(datePicture(ymd, t), N, random));
  const chain = targetsFor(chainPicture(t), N, random);
  const result = targetsFor(resultPicture(t), N, random);

  const pos = new Float32Array(N * 3);
  const vel = new Float32Array(N * 3);
  const col = new Float32Array(N * 3);
  const size = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    // start as a wide, faint cloud
    const a = random() * Math.PI * 2;
    const r = 0.5 + random() * 1.4;
    pos[i * 3] = Math.cos(a) * r * 1.3;
    pos[i * 3 + 1] = Math.sin(a) * r * 0.6;
    pos[i * 3 + 2] = (random() - 0.5) * 1.2;
    col[i * 3] = col[i * 3 + 1] = col[i * 3 + 2] = 0.25;
    size[i] = (mobile ? 2.5 : 2.2) * (0.75 + random() * 0.5);
  }
  const geometry = new THREE.BufferGeometry();
  const posAttr = new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage);
  const colAttr = new THREE.BufferAttribute(col, 3).setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute('position', posAttr);
  geometry.setAttribute('color', colAttr);
  geometry.setAttribute('size', new THREE.BufferAttribute(size, 1));
  const pixelRatio = Math.min(window.devicePixelRatio || 1, 1.75);
  const material = new THREE.ShaderMaterial({
    uniforms: { uPixelRatio: { value: pixelRatio }, uSizeScale: { value: 1 }, uHalo: { value: 0.38 } },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geometry, material);
  points.frustumCulled = false;

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(pixelRatio);
  renderer.setClearColor(0x000000, 0);
  const scene = new THREE.Scene();
  scene.add(points);
  const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
  camera.position.set(0, 0, 10);

  // normalised picture → world: scale and offset, per viewport
  let scale = 1;
  let offX = 0;
  let offY = 0;
  let viewW = 1;
  let viewH = 1;
  function layout() {
    const r = canvas.getBoundingClientRect();
    renderer.setSize(Math.max(1, r.width), Math.max(1, r.height), false);
    camera.aspect = r.width / Math.max(1, r.height);
    camera.updateProjectionMatrix();
    viewH = 2 * 10 * Math.tan(THREE.MathUtils.degToRad(35 / 2));
    viewW = viewH * camera.aspect;
    const wide = camera.aspect > 1.1;
    scale = wide ? Math.min(viewW * 0.27, viewH * 0.66) : Math.min(viewW * 0.47, viewH * 0.3);
    offX = wide ? viewW * 0.2 : 0;
    offY = wide ? -viewH * 0.02 : -viewH * 0.25;
    if (reduced) renderStill();
  }

  // the numbers need small, crisp points; the date and the curve can glow
  const LOOK = { 0: [1.15, 0.36], 1: [0.95, 0.2], 2: [1.0, 0.3] };
  let look = LOOK[0];
  let target = dates[0];
  let mouseX = 99;
  let mouseY = 99;
  let mouseOn = 0;
  let mouseTarget = 0;
  const section = canvas.closest('section');
  if (!reduced) {
    section.addEventListener('pointermove', (e) => {
      const r = canvas.getBoundingClientRect();
      mouseX = (((e.clientX - r.left) / r.width) * 2 - 1) * (viewW / 2);
      mouseY = (1 - ((e.clientY - r.top) / r.height) * 2) * (viewH / 2);
      mouseTarget = 1;
    });
    section.addEventListener('pointerleave', () => (mouseTarget = 0));
  }

  // physics: a spring to the target, damping, the pointer's push and a kick on change
  const K = 0.055;
  const DAMP = 0.84;
  function kick(strength) {
    for (let i = 0; i < N; i++) {
      vel[i * 3] += (random() - 0.5) * strength;
      vel[i * 3 + 1] += (random() - 0.5) * strength;
      vel[i * 3 + 2] += (random() - 0.5) * strength * 0.6;
    }
  }
  function simulate(dt) {
    const s = Math.min(2.5, dt / 16.7);
    const damp = Math.pow(DAMP, s);
    const tp = target.pos;
    const tc = target.col;
    const R2 = 0.42;
    for (let i = 0; i < N; i++) {
      const o = i * 3;
      const tx = offX + tp[o] * scale;
      const ty = offY + tp[o + 1] * scale;
      const tz = tp[o + 2] * scale;
      let fx = (tx - pos[o]) * K;
      let fy = (ty - pos[o + 1]) * K;
      const fz = (tz - pos[o + 2]) * K;
      if (mouseOn > 0.01) {
        const dx = pos[o] - mouseX;
        const dy = pos[o + 1] - mouseY;
        const d2 = dx * dx + dy * dy;
        if (d2 < R2 * 4) {
          const f = (mouseOn * 0.03 * Math.exp(-d2 / R2)) / (Math.sqrt(d2) + 0.05);
          fx += dx * f;
          fy += dy * f;
        }
      }
      vel[o] = (vel[o] + fx * s) * damp;
      vel[o + 1] = (vel[o + 1] + fy * s) * damp;
      vel[o + 2] = (vel[o + 2] + fz * s) * damp;
      pos[o] += vel[o] * s;
      pos[o + 1] += vel[o + 1] * s;
      pos[o + 2] += vel[o + 2] * s;
      col[o] += (tc[o] - col[o]) * 0.06 * s;
      col[o + 1] += (tc[o + 1] - col[o + 1]) * 0.06 * s;
      col[o + 2] += (tc[o + 2] - col[o + 2]) * 0.06 * s;
    }
    posAttr.needsUpdate = true;
    colAttr.needsUpdate = true;
  }
  function renderStill() {
    // reduced motion: the result, in place, without animation
    material.uniforms.uSizeScale.value = LOOK[2][0];
    material.uniforms.uHalo.value = LOOK[2][1];
    const tp = result.pos;
    for (let i = 0; i < N * 3; i += 3) {
      pos[i] = offX + tp[i] * scale;
      pos[i + 1] = offY + tp[i + 1] * scale;
      pos[i + 2] = tp[i + 2] * scale;
    }
    col.set(result.col);
    posAttr.needsUpdate = true;
    colAttr.needsUpdate = true;
    renderer.render(scene, camera);
  }
  layout();
  addEventListener('resize', layout);

  // timeline: the dates rewind to yesterday, then the chain, then the result
  const DATE_MS = 1150;
  const HOLD = { 0: 2600, 1: 6200, 2: 6200 };
  const STEP_MS = { 0: DATE_MS * (dates.length - 1) + HOLD[0], 1: HOLD[1], 2: HOLD[2] };
  let step = 0;
  let dateIndex = 0;
  let stepStart = 0;
  let manualUntil = 0;
  function goTo(next, manual = false) {
    step = next;
    dateIndex = 0;
    stepStart = performance.now();
    target = next === 0 ? dates[0] : next === 1 ? chain : result;
    look = LOOK[next];
    kick(next === 0 ? 0.05 : 0.09);
    if (manual) manualUntil = stepStart + 12000;
    onStep?.(next, STEP_MS[next]);
  }

  let visible = false;
  let started = false;
  let raf = 0;
  let last = 0;
  function frame() {
    raf = requestAnimationFrame(frame);
    if (!visible) return;
    const now = performance.now();
    if (!started) {
      started = true;
      stepStart = now;
      onStep?.(0, STEP_MS[0]);
    }
    const dt = last ? now - last : 16.7;
    last = now;
    // the eases follow elapsed time too, not the screen's refresh rate
    const ease = (k) => 1 - Math.pow(1 - k, Math.min(2.5, dt / 16.7));
    mouseOn += (mouseTarget - mouseOn) * ease(0.08);
    const u = material.uniforms;
    u.uSizeScale.value += (look[0] - u.uSizeScale.value) * ease(0.06);
    u.uHalo.value += (look[1] - u.uHalo.value) * ease(0.06);
    const elapsed = now - stepStart;
    if (step === 0) {
      const k = Math.min(dates.length - 1, Math.floor(elapsed / DATE_MS));
      if (k !== dateIndex) {
        dateIndex = k;
        target = dates[k];
        kick(0.035);
      }
    }
    if (elapsed > STEP_MS[step] && now > manualUntil) goTo((step + 1) % 3);
    simulate(dt);
    renderer.render(scene, camera);
  }
  new IntersectionObserver(
    (entries) => {
      visible = entries.some((e) => e.isIntersecting);
      if (!visible) last = 0;
      if (visible && !raf && !reduced) raf = requestAnimationFrame(frame);
    },
    { threshold: 0.2 },
  ).observe(section);
  if (reduced) {
    started = true;
    visible = true;
    onStep?.(2, STEP_MS[2]);
    renderStill();
  }

  return {
    goTo: (k) => {
      if (reduced) return;
      goTo(k, true);
    },
  };
}
